const SCRIPT_SRC = 'https://unpkg.com/bpmn-js@14.0.0/dist/bpmn-modeler.development.js';
const STYLE_HREFS = [
  'https://unpkg.com/bpmn-js@14.0.0/dist/assets/diagram-js.css',
  'https://unpkg.com/bpmn-js@14.0.0/dist/assets/bpmn-js.css',
  'https://unpkg.com/bpmn-js@14.0.0/dist/assets/bpmn-font/css/bpmn-embedded.css'
];

let loading: Promise<void> | null = null;
const styleLoads = new Map<string, Promise<void>>();

export function loadBpmnJs(src = SCRIPT_SRC): Promise<void> {
  if (window.BpmnJS) {
    return Promise.resolve();
  }

  if (loading) {
    return loading;
  }

  loading = new Promise<void>((resolve, reject) => {
    let settled = false;

    const succeed = () => {
      if (settled) {
        return;
      }

      if (!window.BpmnJS) {
        settled = true;
        loading = null;
        retireFailedScript();
        reject(new Error('bpmn_js_not_loaded'));
        return;
      }

      settled = true;
      resolve();
    };

    const fail = () => {
      if (settled) {
        return;
      }

      settled = true;
      loading = null;
      retireFailedScript();
      reject(new Error('script_load_failed'));
    };

    const attach = (script: HTMLScriptElement) => {
      const state = script.dataset['bpmnState'];
      if (state === 'ready' || window.BpmnJS) {
        script.dataset['bpmnState'] = 'ready';
        succeed();
        return;
      }

      const onLoad = () => {
        script.dataset['bpmnState'] = window.BpmnJS ? 'ready' : 'error';
        succeed();
      };
      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', () => {
        script.dataset['bpmnState'] = 'error';
        fail();
      }, { once: true });

      if (window.BpmnJS) {
        script.dataset['bpmnState'] = 'ready';
        succeed();
        return;
      }

      const readyState = (script as HTMLScriptElement & { readyState?: string }).readyState;
      if (readyState === 'complete' || readyState === 'loaded') {
        onLoad();
        return;
      }

      queueMicrotask(() => {
        if (window.BpmnJS) {
          script.dataset['bpmnState'] = 'ready';
          succeed();
        }
      });
    };

    const existing = document.querySelector(`script[data-bpmn='true']`) as HTMLScriptElement | null;
    if (existing?.dataset['bpmnState'] === 'error') {
      existing.remove();
    } else if (existing) {
      attach(existing);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset['bpmn'] = 'true';
    attach(script);
    document.head.appendChild(script);
  });

  return loading;
}

export function loadBpmnStyles(): Promise<void> {
  return Promise.all(STYLE_HREFS.map(href => loadBpmnStyle(href))).then(() => undefined);
}

function loadBpmnStyle(href: string): Promise<void> {
  const inFlight = styleLoads.get(href);
  if (inFlight) {
    return inFlight;
  }

  const request = new Promise<void>((resolve, reject) => {
    let settled = false;

    const succeed = () => {
      if (settled) {
        return;
      }

      settled = true;
      resolve();
    };

    const fail = () => {
      if (settled) {
        return;
      }

      settled = true;
      styleLoads.delete(href);
      reject(new Error('style_load_failed'));
    };

    const listen = (link: HTMLLinkElement) => {
      link.addEventListener('load', () => {
        link.dataset['bpmnStyleState'] = 'ready';
        succeed();
      }, { once: true });
      link.addEventListener('error', () => {
        link.dataset['bpmnStyleState'] = 'error';
        link.remove();
        fail();
      }, { once: true });
    };

    const completeIfReady = (link: HTMLLinkElement) => {
      if (!link.isConnected) {
        return false;
      }

      if (link.dataset['bpmnStyleState'] === 'ready' || hasLoadedSheet(link)) {
        link.dataset['bpmnStyleState'] = 'ready';
        succeed();
        return true;
      }

      return false;
    };

    const existing = document.querySelector(`link[data-bpmn-style="${href}"]`) as HTMLLinkElement | null;
    if (existing?.dataset['bpmnStyleState'] === 'error') {
      existing.remove();
    } else if (existing) {
      if (!completeIfReady(existing)) {
        listen(existing);
        completeIfReady(existing);
      }
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset['bpmnStyle'] = href;
    listen(link);
    document.head.appendChild(link);
    completeIfReady(link);
  });

  styleLoads.set(href, request);
  return request;
}

function hasLoadedSheet(link: HTMLLinkElement): boolean {
  try {
    if (link.sheet) {
      return true;
    }
  } catch {
    // cross-origin stylesheets can throw when reading sheet
  }

  return [...document.styleSheets].some(sheet => sheet.href === link.href);
}

function retireFailedScript(): void {
  const existing = document.querySelector(`script[data-bpmn='true']`) as HTMLScriptElement | null;
  if (existing?.dataset['bpmnState'] === 'error') {
    existing.remove();
  }
}
